import { sessionManager } from "@/utils/sessionManager";

import { type GetAccessTokenFn, getBaseURL } from "./apiService";

export interface StreamChatMessageParams {
  caseId: string;
  conversationId: string;
  question: string;
  getAccessToken: GetAccessTokenFn;
  onDelta: (delta: string) => void;
  onTitle?: (title: string) => void;
  signal?: AbortSignal;
}

// A frame is one or more `event:`/`data:` lines separated from the next frame by a blank line.
// See demanda/server/api/v2/cases_chat.py's event_stream() for the exact bytes this parses.
function parseSSEFrame(frame: string): { event?: string; data?: string } {
  let event: string | undefined;
  const dataLines: string[] = [];
  for (const line of frame.split("\n")) {
    if (line.startsWith("event:")) {
      event = line.slice("event:".length).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice("data:".length).trim());
    }
  }
  return { event, data: dataLines.length > 0 ? dataLines.join("\n") : undefined };
}

// EventSource can't be used here since the endpoint is a POST with a JSON body and
// auth headers, so this streams via fetch()'s ReadableStream instead.
export async function streamChatMessage({
  caseId,
  conversationId,
  question,
  getAccessToken,
  onDelta,
  onTitle,
  signal,
}: StreamChatMessageParams): Promise<void> {
  const tokens = await getAccessToken();
  if (!tokens?.access_token) {
    throw new Error("No valid session");
  }

  const response = await fetch(
    `${getBaseURL("v2")}/cases/${caseId}/chat/${conversationId}/messages`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${tokens.access_token}`,
        ...sessionManager.getSessionHeaders(),
      },
      body: JSON.stringify({ question }),
      signal,
    }
  );

  if (!response.ok || !response.body) {
    throw new Error(`Chat stream request failed with status ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      // Reader closed without an event: done/error frame — the connection dropped mid-stream.
      throw new Error("Chat stream ended unexpectedly before completion.");
    }

    buffer += decoder.decode(value, { stream: true });

    let separatorIndex = buffer.indexOf("\n\n");
    while (separatorIndex !== -1) {
      const frame = buffer.slice(0, separatorIndex);
      buffer = buffer.slice(separatorIndex + 2);

      const { event, data } = parseSSEFrame(frame);
      if (event === "done") {
        return;
      }
      if (event === "error") {
        const parsed = data ? JSON.parse(data) : {};
        throw new Error(parsed.message || "Failed to generate a response.");
      }
      if (data) {
        const parsed = JSON.parse(data);
        if (typeof parsed.delta === "string") {
          onDelta(parsed.delta);
        }
        if (typeof parsed.title === "string") {
          onTitle?.(parsed.title);
        }
      }

      separatorIndex = buffer.indexOf("\n\n");
    }
  }
}
